import React, {
  useState,
  useRef,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import classNames from "classnames";
import styles from "./style.module.scss";
import XIcon from "assets/icons/XIcon";
import { connect, useDispatch } from "react-redux";
import { createSavedView, fetchSavedViews } from "redux/SingleDataset/actions";
import { useParams } from "react-router";
import { Id, toast } from "react-toastify";
import VLToastComponent from "views/components/VLToastComponent";
import MonoButton from "views/atomComponents/MonochromeButton";
import { amplitudeTrack } from "helpers/utility/amplitude";
import { AMP_SAVED_VIEWS_EVENT__VIEW_CREATED } from "helpers/constants/amplitudeEvents";
import { SavedView } from "types";
import { State } from "redux/store";
import { getSavedViews } from "redux/SingleDataset/selectors";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";

interface SaveViewMenuProps {
  savedViews: SavedView[];
  onCloseMenu: () => void;
}

const SaveViewMenu = ({ savedViews, onCloseMenu }: SaveViewMenuProps) => {
  const savedViewNames = useMemo(
    () => savedViews.map((view: any) => view.name),
    [savedViews]
  );
  const [viewName, setViewName] = useState<string>("");
  const [savingViewInProgress, setSavingViewInProgress] =
    useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const toastIdRef = useRef<any>(null);
  const dispatch = useDispatch();
  const { datasetId, clusterId } = useParams();
  const queryParamsFilters = useAllQueryParamsFilters();

  const saveButtonClassnames = classNames(styles.saveButton, {
    [styles.disabledButton]: !!error || viewName.trim() === "",
  });

  useEffect(() => {
    dispatch(fetchSavedViews(datasetId) as any);

    function adjustModalPosition() {
      if (menuRef.current) {
        const modalRect = menuRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        if (modalRect.right > viewportWidth) {
          const overflowAmount = modalRect.right - viewportWidth + 20;
          menuRef.current.style.transform = `translateX(-${overflowAmount}px)`;
        } else {
          menuRef.current.style.transform = "translateX(0)";
        }
      }
    }
    adjustModalPosition();
    window.addEventListener("resize", adjustModalPosition);
    return () => window.removeEventListener("resize", adjustModalPosition);
    //eslint-disable-next-line
  }, []);

  const showToast = (Element: ReactNode): Id => {
    return toast(Element, {
      closeButton: false,
      closeOnClick: false,
      position: "bottom-center",
    });
  };

  const successNotification = () => {
    toastIdRef.current = showToast(
      <VLToastComponent
        status="SUCCESS"
        title={'View "' + viewName + '" saved!'}
        onClose={() => toast.dismiss(toastIdRef.current)}
        description={
          "Your filter has been successfully saved. You can now access it anytime in your Saved Views."
        }
      />
    );
  };

  const handleCloseMenu = useCallback(
    (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onCloseMenu();
      }
    },
    [onCloseMenu]
  );

  const handleViewNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setViewName(event.target.value);
    if (savedViewNames.includes(event.target.value.trim())) {
      setError("This name already exists. Please try with a different name.");
    } else {
      setError(null);
    }
  };

  const handleSaveViewClick = async () => {
    setViewName(viewName.trim());
    setSavingViewInProgress(true);
    const response = await dispatch(
      createSavedView(
        datasetId!,
        viewName,
        queryParamsFilters,
        clusterId
      ) as any
    );
    setSavingViewInProgress(false);
    onCloseMenu();

    if (response.status === 200) {
      amplitudeTrack(AMP_SAVED_VIEWS_EVENT__VIEW_CREATED);
      successNotification();
      dispatch(fetchSavedViews(datasetId) as any);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleCloseMenu);
    return () => document.removeEventListener("mousedown", handleCloseMenu);
  }, [handleCloseMenu]);

  return (
    <div ref={menuRef} className={styles.saveViewMenu}>
      <div className={styles.saveViewHeader}>
        <span>Save view as</span>
        <div
          className={styles.closeButton}
          onClick={onCloseMenu}
          data-testid="close-button"
        >
          <XIcon />
        </div>
      </div>
      <input
        type="text"
        placeholder="Name your view..."
        className={classNames(styles.viewNameInput, {
          [styles.viewNameInputError]: error,
        })}
        value={viewName}
        onChange={handleViewNameChange}
        data-testid="view-name-input"
      />
      {error && <div className={styles.errorText}>{error}</div>}
      <MonoButton
        label="Save"
        data-testid="save-button"
        loading={savingViewInProgress}
        className={saveButtonClassnames}
        disabled={!!error || viewName.trim() === ""}
        onClick={handleSaveViewClick}
      />
      <div className={styles.saveViewDescription}>
        Saving this view will save the current filters
      </div>
    </div>
  );
};

const mapStateToProps = (state: State) => ({
  savedViews: getSavedViews(state),
});

export default connect(mapStateToProps)(SaveViewMenu);
